/**
 * 代码验证器 (v1.1.0)
 * 用于检测和防止常见的代码生成错误
 */

import * as path from 'path';
import { ConsoleLogger } from './types.js';
import { validateWorkflow, type WorkflowOperation, type WorkflowValidationResult } from './workflowCatalog.js';

/**
 * 验证结果接口
 */
export interface ValidationResult {
    isValid: boolean;
    errors: ValidationError[];
    warnings: ValidationWarning[];
}

/**
 * 验证错误
 */
export interface ValidationError {
    type: 'syntax' | 'completeness' | 'compatibility';
    message: string;
    line?: number;
    suggestion?: string;
}

/**
 * 验证警告
 */
export interface ValidationWarning {
    type: 'best-practice' | 'performance' | 'compatibility' | 'completeness';
    message: string;
    suggestion?: string;
}

export interface WorkflowGateCheck {
    workflowId: string;
    operation: WorkflowOperation;
    skillName?: string;
    params?: Record<string, unknown>;
}

export interface WorkflowGateResult {
    allowed: boolean;
    reason: string;
    validation: WorkflowValidationResult | null;
    state: {
        activeWorkflowId: string | null;
        loadedStandards: string[];
        completedSteps: string[];
    };
}

/**
 * 代码验证器类
 */
export class CodeValidator {
    private logger: ConsoleLogger;
    private standardsLoaded = new Set<string>();
    private workflowProgress = new Map<string, Set<string>>();
    private activeWorkflowId: string | null = null;
    
    constructor(logger?: ConsoleLogger) {
        this.logger = logger || new ConsoleLogger();
    }

    recordStandardsLoad(toolName: string, standardIds: string[]): void {
        const normalized = standardIds
            .map(id => this.normalizeStandardId(id))
            .filter(Boolean);

        if (normalized.length === 0) {
            return;
        }

        normalized.forEach(id => this.standardsLoaded.add(id));
        this.logger.log(`✅ 已记录规范加载: ${toolName} -> ${normalized.join(', ')}`);
    }

    recordWorkflowRecommendation(workflowId: string): void {
        if (!workflowId) {
            return;
        }

        this.activeWorkflowId = workflowId;
        if (!this.workflowProgress.has(workflowId)) {
            this.workflowProgress.set(workflowId, new Set());
        }
    }

    recordWorkflowProgress(workflowId: string, completedSteps: string[] = []): void {
        if (!workflowId) {
            return;
        }

        this.recordWorkflowRecommendation(workflowId);
        const stepSet = this.workflowProgress.get(workflowId) || new Set<string>();
        completedSteps.map(step => String(step).trim()).filter(Boolean).forEach(step => stepSet.add(step));
        this.workflowProgress.set(workflowId, stepSet);
    }

    recordSkillCompletion(skillName: string, params: Record<string, unknown>): void {
        for (const target of this.resolveStepTargets(skillName, params)) {
            this.markWorkflowStepComplete(target.workflowId, target.stepId);
        }
    }

    validateBeforeSkillExecution(check: WorkflowGateCheck): WorkflowGateResult {
        const completedSteps = this.getCompletedSteps(check.workflowId);
        const loadedStandards = Array.from(this.standardsLoaded);
        const state = {
            activeWorkflowId: this.activeWorkflowId,
            loadedStandards,
            completedSteps,
        };
        const validation = validateWorkflow({
            workflowId: check.workflowId,
            operation: check.operation,
            task: this.readStringParam(check.params, 'task'),
            framework: this.readStringParam(check.params, 'framework'),
            currentFile: this.readStringParam(check.params, 'currentFile'),
            completedSteps,
            loadedStandards,
            availableInputs: this.collectAvailableInputs(check.params),
        });

        if (!validation) {
            return {
                allowed: false,
                reason: `未知 workflow: ${check.workflowId}`,
                validation: null,
                state,
            };
        }

        const hasWorkflowContext = this.activeWorkflowId === check.workflowId || this.workflowProgress.has(check.workflowId);
        if (!hasWorkflowContext) {
            return {
                allowed: false,
                reason: `在执行 ${check.skillName || check.operation} 前，尚未进入 ${check.workflowId} workflow。请先调用 recommend_workflow 或 advance_workflow 建立流程上下文。`,
                validation,
                state,
            };
        }

        if (!validation.canProceed) {
            return {
                allowed: false,
                reason: validation.reason,
                validation,
                state,
            };
        }

        return {
            allowed: true,
            reason: '✅ workflow 校验通过',
            validation,
            state,
        };
    }

    /**
     * 验证生成的配置文件内容
     */
    validateConfigContent(content: string): ValidationResult {
        const errors: ValidationError[] = [];
        const warnings: ValidationWarning[] = [];

        // 1. 检查重复标签闭合 (如 </style></style>)
        this.checkDuplicateTags(content, errors);

        // 2. 检查括号匹配
        this.checkBracketMatching(content, errors);

        // 3. 检查 Markdown 语法
        this.checkMarkdownSyntax(content, errors);

        // 4. 检查必要章节完整性
        this.checkRequiredSections(content, warnings);

        // 5. 检查自定义内容标记
        this.checkCustomContentMarkers(content, warnings);

        return {
            isValid: errors.length === 0,
            errors,
            warnings
        };
    }

    /**
     * 检查重复的HTML标签
     */
    private checkDuplicateTags(content: string, errors: ValidationError[]): void {
        const lines = content.split('\n');
        const tagPattern = /<\/([\w-]+)>/g;
        
        for (let i = 0; i < lines.length; i++) {
            const line = lines[i];
            const matches = Array.from(line.matchAll(tagPattern));
            
            if (matches.length > 0) {
                const tags = matches.map(m => m[1]);
                const tagCounts = new Map<string, number>();
                
                tags.forEach(tag => {
                    tagCounts.set(tag, (tagCounts.get(tag) || 0) + 1);
                });
                
                tagCounts.forEach((count, tag) => {
                    if (count > 1) {
                        errors.push({
                            type: 'syntax',
                            message: `重复的闭合标签 </${tag}> 在同一行出现 ${count} 次`,
                            line: i + 1,
                            suggestion: `检查是否有多余的闭合标签，应该只保留一个 </${tag}>`
                        });
                    }
                });
            }
        }
    }

    /**
     * 检查括号匹配
     */
    private checkBracketMatching(content: string, errors: ValidationError[]): void {
        const brackets = {
            '(': ')',
            '[': ']',
            '{': '}',
            '<': '>'
        };
        
        const stack: Array<{ char: string; line: number; col: number }> = [];
        const lines = content.split('\n');
        
        for (let lineNum = 0; lineNum < lines.length; lineNum++) {
            const line = lines[lineNum];
            
            // 跳过代码块内容
            if (line.trim().startsWith('```')) {
                continue;
            }
            
            // 跳过 Markdown blockquote 行（以 > 开头）
            if (line.trim().startsWith('>')) {
                continue;
            }
            
            for (let col = 0; col < line.length; col++) {
                const char = line[col];
                
                // 跳过 Markdown blockquote 的 > 符号
                if (char === '>' && (col === 0 || line[col - 1] === ' ') && 
                    (col === line.length - 1 || line[col + 1] === ' ')) {
                    continue;
                }
                
                if (char in brackets) {
                    stack.push({ char, line: lineNum + 1, col: col + 1 });
                } else if (Object.values(brackets).includes(char)) {
                    if (stack.length === 0) {
                        errors.push({
                            type: 'syntax',
                            message: `未配对的闭合括号 '${char}'`,
                            line: lineNum + 1,
                            suggestion: `检查是否缺少对应的开放括号`
                        });
                    } else {
                        const last = stack.pop()!;
                        const expectedClose = brackets[last.char as keyof typeof brackets];
                        
                        if (char !== expectedClose) {
                            errors.push({
                                type: 'syntax',
                                message: `括号不匹配: 期望 '${expectedClose}' 但得到 '${char}'`,
                                line: lineNum + 1,
                                suggestion: `检查第 ${last.line} 行的 '${last.char}' 对应的闭合括号`
                            });
                        }
                    }
                }
            }
        }
        
        // 检查未闭合的括号
        if (stack.length > 0) {
            stack.forEach(bracket => {
                errors.push({
                    type: 'syntax',
                    message: `未闭合的括号 '${bracket.char}'`,
                    line: bracket.line,
                    suggestion: `添加对应的闭合括号 '${brackets[bracket.char as keyof typeof brackets]}'`
                });
            });
        }
    }

    /**
     * 检查 Markdown 语法
     */
    private checkMarkdownSyntax(content: string, errors: ValidationError[]): void {
        const lines = content.split('\n');
        let inCodeBlock = false;
        let codeBlockStart = 0;
        
        for (let i = 0; i < lines.length; i++) {
            const line = lines[i];
            
            // 检查代码块标记
            if (line.trim().startsWith('```')) {
                if (inCodeBlock) {
                    inCodeBlock = false;
                } else {
                    inCodeBlock = true;
                    codeBlockStart = i + 1;
                }
            }
        }
        
        // 如果代码块未闭合
        if (inCodeBlock) {
            errors.push({
                type: 'syntax',
                message: '未闭合的代码块',
                line: codeBlockStart,
                suggestion: '在代码块末尾添加 ``` 闭合标记'
            });
        }
    }

    /**
     * 检查必要章节完整性
     */
    private checkRequiredSections(content: string, warnings: ValidationWarning[]): void {
        const requiredSections = [
            { pattern: /## ⚠️ 强制工作流/i, name: '强制工作流章节' },
            { pattern: /## 📚 配置的 Agents/i, name: 'Agents 配置章节' }
        ];
        
        requiredSections.forEach(section => {
            if (!section.pattern.test(content)) {
                warnings.push({
                    type: 'completeness',
                    message: `缺少必要章节: ${section.name}`,
                    suggestion: '确保生成的配置文件包含所有必要的章节'
                });
            }
        });
    }

    /**
     * 检查自定义内容标记
     */
    private checkCustomContentMarkers(content: string, warnings: ValidationWarning[]): void {
        const hasCustomStart = content.includes('<!-- CUSTOM_START -->');
        const hasCustomEnd = content.includes('<!-- CUSTOM_END -->');
        
        if (hasCustomStart !== hasCustomEnd) {
            warnings.push({
                type: 'completeness',
                message: 'CUSTOM 标记不完整',
                suggestion: '确保 CUSTOM_START 和 CUSTOM_END 成对出现'
            });
        }
    }

    /**
     * 验证 Agent 内容
     */
    validateAgentContent(content: string, agentId: string): ValidationResult {
        const errors: ValidationError[] = [];
        const warnings: ValidationWarning[] = [];

        // 检查 Agent 文件的特殊要求
        if (!content.includes('⚠️ 强制工作流') && !content.includes('## 强制工作流')) {
            warnings.push({
                type: 'best-practice',
                message: `Agent ${agentId} 缺少强制工作流说明`,
                suggestion: '建议在 Agent 中包含 MCP 工具调用的强制说明'
            });
        }

        // 基本语法检查
        const syntaxValidation = this.validateConfigContent(content);
        errors.push(...syntaxValidation.errors);
        warnings.push(...syntaxValidation.warnings);

        return {
            isValid: errors.length === 0,
            errors,
            warnings
        };
    }

    /**
     * 生成验证报告
     */
    generateValidationReport(result: ValidationResult): string {
        let report = '';
        
        if (result.isValid && result.warnings.length === 0) {
            report = '✅ 验证通过，未发现问题\n';
            return report;
        }
        
        if (!result.isValid) {
            report += '❌ 验证失败，发现以下错误：\n\n';
            result.errors.forEach((error, index) => {
                report += `${index + 1}. [${error.type}] ${error.message}\n`;
                if (error.line) {
                    report += `   位置: 第 ${error.line} 行\n`;
                }
                if (error.suggestion) {
                    report += `   建议: ${error.suggestion}\n`;
                }
                report += '\n';
            });
        } else {
            report += '✅ 验证通过\n\n';
        }
        
        if (result.warnings.length > 0) {
            report += '⚠️ 发现以下警告：\n\n';
            result.warnings.forEach((warning, index) => {
                report += `${index + 1}. [${warning.type}] ${warning.message}\n`;
                if (warning.suggestion) {
                    report += `   建议: ${warning.suggestion}\n`;
                }
                report += '\n';
            });
        }
        
        return report;
    }

    /**
     * 尝试自动修复简单的语法错误
     */
    attemptAutoFix(content: string): { fixed: boolean; content: string; changes: string[] } {
        let fixedContent = content;
        const changes: string[] = [];

        // 1. 修复重复的闭合标签
        const tagPattern = /(<\/([\w-]+)>)\1+/g;
        const tagMatches = content.match(tagPattern);
        if (tagMatches) {
            fixedContent = fixedContent.replace(tagPattern, '$1');
            changes.push(`修复了重复的闭合标签: ${tagMatches.join(', ')}`);
        }

        // 2. 修复未闭合的代码块
        const codeBlockCount = (fixedContent.match(/```/g) || []).length;
        if (codeBlockCount % 2 !== 0) {
            fixedContent += '\n```\n';
            changes.push('添加了缺失的代码块闭合标记');
        }

        return {
            fixed: changes.length > 0,
            content: fixedContent,
            changes
        };
    }

    private normalizeStandardId(value: string): string {
        const trimmed = value.trim();
        if (!trimmed) {
            return '';
        }

        const withoutQuery = trimmed.split('?')[0].split('#')[0];
        const baseName = path.basename(withoutQuery);
        return baseName.endsWith('.md') ? baseName.slice(0, -3) : baseName;
    }

    private readStringParam(params: Record<string, unknown> | undefined, key: string): string | undefined {
        const value = params?.[key];
        return typeof value === 'string' && value.trim() ? value : undefined;
    }

    private collectAvailableInputs(params?: Record<string, unknown>): string[] {
        if (!params) {
            return [];
        }

        const inputs = new Set<string>();
        for (const [key, value] of Object.entries(params)) {
            if (value === undefined || value === null) {
                continue;
            }
            if (typeof value === 'string' && value.trim() === '') {
                continue;
            }
            if (Array.isArray(value) && value.length === 0) {
                continue;
            }
            inputs.add(key);
        }

        if (inputs.has('workspacePath')) {
            inputs.add('projectPath');
        }
        if (inputs.has('projectPath')) {
            inputs.add('workspacePath');
        }

        return [...inputs];
    }

    private getCompletedSteps(workflowId: string): string[] {
        return Array.from(this.workflowProgress.get(workflowId) || new Set<string>());
    }

    private markWorkflowStepComplete(workflowId: string, stepId: string): void {
        if (!workflowId || !stepId) {
            return;
        }

        this.recordWorkflowRecommendation(workflowId);
        const stepSet = this.workflowProgress.get(workflowId) || new Set<string>();
        stepSet.add(stepId);
        this.workflowProgress.set(workflowId, stepSet);
    }

    private resolveStepTargets(skillName: string, params: Record<string, unknown>): Array<{ workflowId: string; stepId: string }> {
        const trackedWorkflows = [...new Set([this.activeWorkflowId, ...this.workflowProgress.keys()].filter(Boolean) as string[])];
        const targets: Array<{ workflowId: string; stepId: string }> = [];
        const action = typeof params.action === 'string' ? params.action : undefined;

        for (const workflowId of trackedWorkflows) {
            switch (workflowId) {
                case 'sketch_restoration':
                    if (skillName === 'get_standards') targets.push({ workflowId, stepId: 'load-restoration-standards' });
                    if (skillName === 'sketch_measure' && (!action || String(params.cmd || '').trim() === '' || params.cmd === 'compact')) {
                        targets.push({ workflowId, stepId: 'measure-compact' });
                    }
                    if (skillName === 'validate_restoration') targets.push({ workflowId, stepId: 'validate-output' });
                    if (skillName === 'query_cases') targets.push({ workflowId, stepId: 'fallback-cases' });
                    break;
                case 'problem_diagnosis':
                    if (skillName === 'troubleshoot') targets.push({ workflowId, stepId: 'semantic-diagnosis' });
                    if (skillName === 'query_cases') targets.push({ workflowId, stepId: 'query-verified-cases' });
                    if (skillName === 'get_standards') targets.push({ workflowId, stepId: 'load-targeted-standards' });
                    break;
                case 'standards_setup':
                    if (skillName === 'analyze_project') targets.push({ workflowId, stepId: 'analyze-project' });
                    if (skillName === 'manage_agents' && (action === 'match' || params.projectFeatures)) {
                        targets.push({ workflowId, stepId: 'match-agents' });
                    }
                    if (skillName === 'setup_project' && (action === 'generate' || action === 'auto_setup' || action === undefined)) {
                        targets.push({ workflowId, stepId: 'setup-project' });
                    }
                    if (skillName === 'get_standards') targets.push({ workflowId, stepId: 'load-minimal-standards' });
                    break;
                case 'customization_maintenance':
                    if (skillName === 'health_check') targets.push({ workflowId, stepId: 'inspect-customization-health' });
                    if (skillName === 'analyze_project') targets.push({ workflowId, stepId: 'inventory-customization-surface' });
                    if (skillName === 'get_template') targets.push({ workflowId, stepId: 'locate-reusable-templates' });
                    if (skillName === 'setup_project' && (action === 'generate' || action === 'auto_setup' || action === undefined)) {
                        targets.push({ workflowId, stepId: 'regenerate-customization-config' });
                    }
                    break;
                case 'reference_clone':
                    if (skillName === 'clone_analyze') targets.push({ workflowId, stepId: 'analyze-reference' });
                    if (skillName === 'clone_generate') targets.push({ workflowId, stepId: 'generate-skeleton' });
                    if (skillName === 'get_template') targets.push({ workflowId, stepId: 'supplement-templates' });
                    break;
                case 'multi_step_reasoning':
                    if (skillName === 'analyze_project') targets.push({ workflowId, stepId: 'analyze-context' });
                    break;
                default:
                    break;
            }
        }

        return targets;
    }
}

/**
 * 创建默认验证器实例
 */
export function createValidator(logger?: ConsoleLogger): CodeValidator {
    return new CodeValidator(logger);
}
